import { StackProps } from 'atoms/Layout/Stack/Stack';
import React, { useEffect } from 'react';
import { WistiaPlayerContainer } from './WistiaPlayer.styles';

interface WistiaPlayerProps extends StackProps {
  videoId: string;
  containerId: string;
  autoPlay?: boolean;
}

interface FormatterWistiaInnerHTMLOptions {
  removePlaceholder?: boolean;
}

const getFormattedWistiaInnerHTML = (
  videoId: string,
  { removePlaceholder = false }: FormatterWistiaInnerHTMLOptions
): string => `
  <div class="wistia_responsive_padding" style="padding:56.25% 0 0 0;position:relative;">
    <div class="wistia_responsive_wrapper" style="height:100%;left:0;position:absolute;top:0;width:100%;">
      <div class="wistia_embed wistia_async_${videoId} seo=false videoFoam=true" style="height:100%;position:relative;width:100%">
        <div class="wistia_swatch" style="height:100%;left:0;opacity:0;overflow:hidden;position:absolute;top:0;transition:opacity 200ms;width:100%;">
          ${
            removePlaceholder
              ? ''
              : '<img src="https://fast.wistia.com/embed/medias/${videoId}/swatch" style="filter:blur(5px);height:100%;object-fit:contain;width:100%;" alt="" aria-hidden="true" onload="this.parentNode.style.opacity=1;" />'
          }
        </div>
      </div>
    </div>
  </div>
`;

const WistiaPlayer = ({ videoId, containerId, autoPlay = false, ...rest }: WistiaPlayerProps): JSX.Element => {
  const addVideoIdScript = (): HTMLScriptElement => {
    // skip script addition if the script is already present
    const scriptSrc = `https://fast.wistia.com/embed/medias/${videoId}.jsonp`;

    if (Array.from(document.scripts).find((s) => s.src == scriptSrc)) {
      return;
    }

    const scriptElement = document.createElement('script');
    scriptElement.src = scriptSrc;
    scriptElement.async = true;

    return scriptElement;
  };

  const addExternalAssetsScript = (): HTMLScriptElement => {
    // skip script addition if the script is already present
    const scriptSrc = 'https://fast.wistia.com/assets/external/E-v1.js';

    if (Array.from(document.scripts).find((s) => s.src == scriptSrc)) {
      return;
    }

    const scriptElement = document.createElement('script');
    scriptElement.src = scriptSrc;
    scriptElement.async = true;

    return scriptElement;
  };

  useEffect(() => {
    const videoIdScript = addVideoIdScript();
    const externalAssetScriptElement = addExternalAssetsScript();

    const wistiaContainer = document.createElement('div');
    wistiaContainer.innerHTML = getFormattedWistiaInnerHTML(videoId, { removePlaceholder: autoPlay });

    const container = document.getElementById(containerId);

    if (videoIdScript) {
      container.appendChild(videoIdScript);
    }
    if (externalAssetScriptElement) {
      container.appendChild(externalAssetScriptElement);
    }
    container.appendChild(wistiaContainer);

    return () => {
      // Cleanup code
      container.innerHTML = '';
    };
  }, []);

  return (
    <WistiaPlayerContainer
      id={`${containerId}`}
      {...rest}
    />
  );
};

export default WistiaPlayer;
